/**
 * Live updates on the cell build (move map M28 ← M17): one AppSync Events socket for the tab, channels
 * /loc/{location}/{topic} (location written with dashes). The channel's handler admits only locations the Cognito
 * token names (location_ids), so a pro hears only their own studios. Notices carry ids and a few filter keys (pro_id
 * on tickets and spotlights), never content: on a notice the hub re-reads through the API, as it re-fetched on a
 * Supabase change.
 *
 * Supabase published 7 tables; the hub listens to 4 of them here (tickets, spotlights, suggestion_responses,
 * suggestion_topics). tenants, compliance_records and ticket_events were never published, so those subscriptions
 * never fired on Supabase (M17 design note) — on the cell they are no-ops too, by the same rule.
 */
import { EVENTS } from './config';
import { claimsOf, getCellSession } from './cognito';

export const TOPIC_OF_TABLE: Record<string, string> = {
  tickets: 'tickets', spotlights: 'spotlights', suggestion_responses: 'suggestion-responses', suggestion_topics: 'suggestion-topics',
  messages: 'messages', pipeline_deals: 'pipeline-deals', onboarding_journeys: 'onboarding-journeys',
};

export interface Notice { type: string; id: string; op: string; pro_id?: string; [k: string]: unknown }
type Handler = (n: Notice) => void;

const b64url = (s: string) => btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
let ws: WebSocket | null = null;
let ready: Promise<WebSocket> | null = null;
let seq = 0;
const subs = new Map<string, { channel: string; handlers: Set<Handler>; id: string }>();

async function auth(): Promise<Record<string, string> | null> {
  const s = await getCellSession();
  return s ? { host: EVENTS.httpHost, Authorization: s.idToken } : null;
}

function connect(): Promise<WebSocket> {
  if (ready) return ready;
  ready = (async () => {
    const a = await auth();
    if (!a || !EVENTS.realtimeHost) throw new Error('live updates unavailable');
    const sock = new WebSocket(`wss://${EVENTS.realtimeHost}/event/realtime`, ['aws-appsync-event-ws', `header-${b64url(JSON.stringify(a))}`]);
    await new Promise<void>((resolve, reject) => {
      sock.onopen = () => sock.send(JSON.stringify({ type: 'connection_init' }));
      sock.onerror = () => reject(new Error('live updates socket error'));
      sock.onmessage = (m) => {
        const msg = JSON.parse(String(m.data));
        if (msg.type === 'connection_ack') resolve();
        else if (msg.type === 'connection_error') reject(new Error('live updates refused'));
      };
    });
    sock.onmessage = (m) => {
      const msg = JSON.parse(String(m.data));
      if (msg.type !== 'data') return;
      const sub = [...subs.values()].find((x) => x.id === msg.id);
      if (!sub) return;
      let n: Notice; try { n = JSON.parse(msg.event); } catch { return; }
      sub.handlers.forEach((h) => h(n));
    };
    sock.onclose = () => {
      // Reconnect and re-subscribe; every handler also fires once so the screen back-fills what it missed
      // (the same contract as the Supabase hook: invalidate on re-subscribe).
      ws = null; ready = null;
      const pending = [...subs.values()];
      if (pending.length) setTimeout(() => { for (const s of pending) { s.handlers.forEach((h) => h({ type: 'resubscribe', id: '', op: 'resubscribe' })); void send(s); } }, 2000);
    };
    ws = sock;
    return sock;
  })().catch((e) => { ready = null; throw e; });
  return ready;
}

async function send(s: { channel: string; id: string }) {
  const sock = await connect();
  const a = await auth();
  sock.send(JSON.stringify({ type: 'subscribe', id: s.id, channel: s.channel, authorization: a }));
}

/** The locations this token may listen to (the claim the channel handler checks). */
export async function myLocations(): Promise<string[]> {
  const s = await getCellSession();
  if (!s) return [];
  const v = claimsOf(s.idToken).location_ids;
  if (Array.isArray(v)) return v.map(String);
  if (typeof v === 'string') return v.replace(/^\[|\]$/g, '').split(/[\s,]+/).filter(Boolean);
  return [];
}

/** Listen to one table's notices at one location. Returns the unsubscribe. */
export function listen(location: string, table: string, handler: Handler): () => void {
  const topic = TOPIC_OF_TABLE[table];
  if (!topic || !/^[a-z0-9_]{1,50}$/.test(location)) return () => {};
  const channel = `/loc/${location.replace(/_/g, '-')}/${topic}`;
  let s = subs.get(channel);
  if (!s) {
    s = { channel, handlers: new Set(), id: `sub-${++seq}` };
    subs.set(channel, s);
    void send(s).catch(() => { /* no live updates: react-query's own refetching still applies */ });
  }
  s.handlers.add(handler);
  const mine = s;
  return () => {
    mine.handlers.delete(handler);
    if (mine.handlers.size === 0) {
      subs.delete(channel);
      if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'unsubscribe', id: mine.id }));
    }
  };
}
